import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Card,
  Grid,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  Alert,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  IconButton,
  Stack,
  Tabs,
  Tab,
  InputAdornment,
  TablePagination,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  PlayArrow as PlayArrowIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  ContentCopy as ContentCopyIcon,
  Speed as SpeedIcon,
  FilterList as FilterListIcon,
  CallSplit as CallSplitIcon,
  Code as CodeIcon,
  Tune as TuneIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Http as HttpIcon,
  Phone as PhoneIcon,
} from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useMessageRedirectRulesService } from "../services/messageRedirectRules.service";

// Opciones de tipo de regla con descripciones y colores representativos
const RULE_TYPES = [
  {
    value: "all",
    label: "Espejo Global (All)",
    desc: "Reenvía absolutamente todos los mensajes entrantes sin filtro de texto.",
    color: "#0284c7",
    bg: "#e0f2fe",
  },
  {
    value: "text_contains",
    label: "Contiene Texto (Contains)",
    desc: "Coincidencia parcial si contiene la palabra clave o elementos de la lista.",
    color: "#0891b2",
    bg: "#cffafe",
  },
  {
    value: "text_regex",
    label: "Expresión Regular (Regex)",
    desc: "Evalúa una expresión regular avanzada sobre el texto del cliente.",
    color: "#7c3aed",
    bg: "#ede9fe",
  },
  {
    value: "text_exact",
    label: "Texto Exacto (Exact)",
    desc: "Coincidencia 100% idéntica con el texto del cliente.",
    color: "#059669",
    bg: "#d1fae5",
  },
  {
    value: "button_payload",
    label: "Payload de Botón",
    desc: "Coincide con el payload del botón pulsado por el usuario en WhatsApp.",
    color: "#ea580c",
    bg: "#ffedd5",
  },
  {
    value: "button_text",
    label: "Texto de Botón",
    desc: "Coincide con el texto / título visual del botón pulsado.",
    color: "#d97706",
    bg: "#fef3c7",
  },
  {
    value: "interactive",
    label: "Mensaje Interactivo",
    desc: "Coincide con respuestas de listas o botones interactivos.",
    color: "#db2777",
    bg: "#fce7f3",
  },
  {
    value: "any_match",
    label: "Cualquier Coincidencia (Any)",
    desc: "Busca coincidencia flexible en texto, título de botón o payload.",
    color: "#4f46e5",
    bg: "#e0e7ff",
  },
];

// Tipos de mensaje soportados por WhatsApp
const AVAILABLE_MESSAGE_TYPES = [
  { id: "text", label: "Texto" },
  { id: "button", label: "Botón" },
  { id: "interactive", label: "Interactivo" },
  { id: "image", label: "Imagen" },
  { id: "document", label: "Documento" },
  { id: "audio", label: "Audio" },
  { id: "video", label: "Video" },
  { id: "location", label: "Ubicación" },
  { id: "contacts", label: "Contactos" },
];

const INITIAL_RULE_FORM = {
  name: "",
  description: "",
  targetUrl: "",
  isActive: true,
  httpMethod: "POST",
  headers: [{ key: "Content-Type", value: "application/json" }],
  ruleType: "text_regex",
  matchPattern: "",
  matchPatternList: [],
  isRegex: true,
  regexFlags: "i",
  caseSensitive: false,
  messageTypes: ["text", "button"],
  senderFilter: "",
  payloadFormat: "raw",
  timeoutMs: 5000,
  stopOnMatch: false,
};

export default function MessageRedirectRules() {
  const redirectRulesService = useMessageRedirectRulesService();

  // Estados de datos
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Estados de filtros y paginación
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // 'all' | 'true' | 'false'
  const [typeFilter, setTypeFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Estados del modal de edición / creación
  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentRuleId, setCurrentRuleId] = useState(null);
  const [formData, setFormData] = useState(INITIAL_RULE_FORM);
  const [modalTab, setModalTab] = useState(0);
  const [keywordInput, setKeywordInput] = useState("");
  const [savingRule, setSavingRule] = useState(false);

  // Estados para prueba de conexión (Test Webhook)
  const [testResult, setTestResult] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [testingRowId, setTestingRowId] = useState(null);

  // Estados para diálogo de eliminación
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);
  const [deletingRule, setDeletingRule] = useState(false);

  // Cargar reglas desde la API (solo al abrir la vista o tras mutaciones)
  const fetchRules = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await redirectRulesService.getRules();
      // El backend puede devolver un array o un objeto { rules, total }
      let list = [];
      if (Array.isArray(data)) {
        list = data;
      } else if (data && Array.isArray(data.rules)) {
        list = data.rules;
      } else if (data && Array.isArray(data.data)) {
        list = data.data;
      }
      setRules(list);
    } catch (err) {
      console.error("Error al cargar reglas:", err);
      setError("No se pudieron cargar las reglas de redirección. Verifique la conexión con el servidor.");
      toast.error("Error al cargar reglas de redirección");
    } finally {
      setLoading(false);
    }
  }, [redirectRulesService]);

  // Carga inicial al abrir la vista
  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  // Filtrado 100% en memoria (sin peticiones HTTP al buscar o filtrar)
  const filteredRules = useMemo(() => {
    return rules.filter((rule) => {
      // Filtro por estado
      if (statusFilter !== "all") {
        const isAct = statusFilter === "true";
        if (Boolean(rule.isActive) !== isAct) return false;
      }
      // Filtro por tipo de regla
      if (typeFilter !== "all" && rule.ruleType !== typeFilter) {
        return false;
      }
      // Filtro por texto de búsqueda
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase().trim();
        const nameMatch = (rule.name || "").toLowerCase().includes(term);
        const descMatch = (rule.description || "").toLowerCase().includes(term);
        const urlMatch = (rule.targetUrl || "").toLowerCase().includes(term);
        const patternMatch = (rule.matchPattern || "").toLowerCase().includes(term);
        const listMatch =
          Array.isArray(rule.matchPatternList) &&
          rule.matchPatternList.some((k) => (k || "").toLowerCase().includes(term));
        if (!nameMatch && !descMatch && !urlMatch && !patternMatch && !listMatch) {
          return false;
        }
      }
      return true;
    });
  }, [rules, searchTerm, statusFilter, typeFilter]);

  // Estadísticas rápidas
  const stats = useMemo(() => {
    const total = rules.length;
    const activeCount = rules.filter((r) => r.isActive).length;
    const inactiveCount = total - activeCount;
    return { total, activeCount, inactiveCount };
  }, [rules]);

  // Manejar apertura de modal para Crear
  const handleOpenCreateModal = () => {
    setIsEditing(false);
    setCurrentRuleId(null);
    setFormData(INITIAL_RULE_FORM);
    setModalTab(0);
    setTestResult(null);
    setKeywordInput("");
    setOpenModal(true);
  };

  // Manejar apertura de modal para Editar
  const handleOpenEditModal = (rule) => {
    setIsEditing(true);
    setCurrentRuleId(rule._id || rule.id);
    setFormData({
      name: rule.name || "",
      description: rule.description || "",
      targetUrl: rule.targetUrl || "",
      isActive: rule.isActive ?? true,
      httpMethod: rule.httpMethod || "POST",
      headers: Array.isArray(rule.headers) && rule.headers.length > 0
        ? rule.headers.map((h) => ({ key: h.key || "", value: h.value || "" }))
        : [{ key: "", value: "" }],
      ruleType: rule.ruleType || "text_regex",
      matchPattern: rule.matchPattern || "",
      matchPatternList: Array.isArray(rule.matchPatternList) ? [...rule.matchPatternList] : [],
      isRegex: rule.isRegex ?? (rule.ruleType === "text_regex"),
      regexFlags: rule.regexFlags || "i",
      caseSensitive: rule.caseSensitive ?? false,
      messageTypes: Array.isArray(rule.messageTypes) ? [...rule.messageTypes] : [],
      senderFilter: rule.senderFilter || "",
      payloadFormat: rule.payloadFormat || "raw",
      timeoutMs: rule.timeoutMs || 5000,
      stopOnMatch: rule.stopOnMatch ?? false,
    });
    setModalTab(0);
    setTestResult(null);
    setKeywordInput("");
    setOpenModal(true);
  };

  // Manejar cambio en inputs simples
  const handleFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Manejo de Headers dinámicos
  const handleHeaderChange = (index, field, value) => {
    setFormData((prev) => {
      const nextHeaders = [...prev.headers];
      nextHeaders[index] = { ...nextHeaders[index], [field]: value };
      return { ...prev, headers: nextHeaders };
    });
  };

  const handleAddHeader = () => {
    setFormData((prev) => ({
      ...prev,
      headers: [...prev.headers, { key: "", value: "" }],
    }));
  };

  const handleRemoveHeader = (index) => {
    setFormData((prev) => {
      const nextHeaders = prev.headers.filter((_, i) => i !== index);
      return { ...prev, headers: nextHeaders.length > 0 ? nextHeaders : [{ key: "", value: "" }] };
    });
  };

  // Manejo de Palabras Clave (Keywords)
  const handleAddKeyword = () => {
    const val = keywordInput.trim();
    if (val && !formData.matchPatternList.includes(val)) {
      setFormData((prev) => ({
        ...prev,
        matchPatternList: [...prev.matchPatternList, val],
      }));
      setKeywordInput("");
    }
  };

  const handleRemoveKeyword = (kwToRemove) => {
    setFormData((prev) => ({
      ...prev,
      matchPatternList: prev.matchPatternList.filter((k) => k !== kwToRemove),
    }));
  };

  // Manejo de Message Types
  const handleToggleMessageType = (typeId) => {
    setFormData((prev) => {
      const exists = prev.messageTypes.includes(typeId);
      const nextTypes = exists
        ? prev.messageTypes.filter((t) => t !== typeId)
        : [...prev.messageTypes, typeId];
      return { ...prev, messageTypes: nextTypes };
    });
  };

  // Probar conexión desde el Modal
  const handleTestConnectionInModal = async () => {
    if (!formData.targetUrl.trim()) {
      toast.warning("Ingrese una URL destino válida antes de realizar la prueba");
      return;
    }

    setTestingConnection(true);
    setTestResult(null);

    const filteredHeaders = formData.headers.filter((h) => h.key && h.key.trim() !== "");

    try {
      const res = await redirectRulesService.testRuleConnection({
        targetUrl: formData.targetUrl.trim(),
        httpMethod: formData.httpMethod,
        timeoutMs: Number(formData.timeoutMs) || 5000,
        headers: filteredHeaders,
      });

      const result = res.testResult || res.data || res;
      setTestResult(result);

      if (result.success || (result.statusCode >= 200 && result.statusCode < 300)) {
        toast.success(`Prueba exitosa (Código ${result.statusCode || 200} en ${result.durationMs || 0}ms)`);
      } else {
        toast.error(`Prueba fallida: ${result.error || `HTTP ${result.statusCode}`}`);
      }
    } catch (err) {
      console.error("Error en test de conexión:", err);
      const errMsg = err.response?.data?.message || err.response?.data?.error || err.message;
      setTestResult({
        success: false,
        statusCode: err.response?.status || 500,
        error: errMsg,
      });
      toast.error(`Fallo de conexión: ${errMsg}`);
    } finally {
      setTestingConnection(false);
    }
  };

  // Probar conexión rápida directamente desde la fila
  const handleTestRowConnection = async (rule) => {
    const id = rule._id || rule.id;
    setTestingRowId(id);
    try {
      const res = await redirectRulesService.testRuleConnection({
        targetUrl: rule.targetUrl,
        httpMethod: rule.httpMethod || "POST",
        timeoutMs: rule.timeoutMs || 5000,
        headers: rule.headers || [],
      });
      const result = res.testResult || res.data || res;
      if (result.success || (result.statusCode >= 200 && result.statusCode < 300)) {
        toast.success(
          `Destino '${rule.name}' respondiendo OK (${result.statusCode || 200}) en ${result.durationMs || 0}ms`
        );
      } else {
        toast.warn(`Respuesta del webhook: ${result.statusCode || "Error"} (${result.error || "Fallo"})`);
      }
    } catch (err) {
      toast.error(`Error conectando a '${rule.name}': ${err.message}`);
    } finally {
      setTestingRowId(null);
    }
  };

  // Alternar Activo/Inactivo (Toggle)
  const handleToggleStatus = async (rule) => {
    const id = rule._id || rule.id;
    const previousState = rule.isActive;
    // Actualización optimista local
    setRules((prev) =>
      prev.map((r) => (r._id === id || r.id === id ? { ...r, isActive: !previousState } : r))
    );

    try {
      await redirectRulesService.toggleRule(id);
      toast.success(`Regla "${rule.name}" ${!previousState ? "activada" : "desactivada"}`);
    } catch (err) {
      console.error("Error al alternar estado:", err);
      // Revertir
      setRules((prev) =>
        prev.map((r) => (r._id === id || r.id === id ? { ...r, isActive: previousState } : r))
      );
      toast.error("No se pudo cambiar el estado de la regla");
    }
  };

  // Guardar Regla (Crear o Editar)
  const handleSaveRule = async () => {
    if (!formData.name.trim()) {
      toast.error("El nombre de la regla es obligatorio");
      setModalTab(0);
      return;
    }
    if (!formData.targetUrl.trim()) {
      toast.error("La URL destino del webhook es obligatoria");
      setModalTab(0);
      return;
    }

    setSavingRule(true);
    try {
      // Limpiar cabeceras vacías
      const cleanHeaders = formData.headers
        .filter((h) => h.key && h.key.trim() !== "")
        .map((h) => ({ key: h.key.trim(), value: h.value }));

      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        targetUrl: formData.targetUrl.trim(),
        isActive: Boolean(formData.isActive),
        httpMethod: formData.httpMethod || "POST",
        headers: cleanHeaders,
        ruleType: formData.ruleType,
        matchPattern: formData.matchPattern || "",
        matchPatternList: formData.matchPatternList || [],
        isRegex: Boolean(formData.isRegex),
        regexFlags: formData.regexFlags || "i",
        caseSensitive: Boolean(formData.caseSensitive),
        messageTypes: formData.messageTypes || [],
        senderFilter: formData.senderFilter ? formData.senderFilter.trim() : "",
        payloadFormat: formData.payloadFormat || "raw",
        timeoutMs: Number(formData.timeoutMs) || 5000,
        stopOnMatch: Boolean(formData.stopOnMatch),
      };

      if (isEditing && currentRuleId) {
        await redirectRulesService.updateRule(currentRuleId, payload);
        toast.success("Regla actualizada exitosamente");
      } else {
        await redirectRulesService.createRule(payload);
        toast.success("Regla creada exitosamente");
      }

      setOpenModal(false);
      fetchRules();
    } catch (err) {
      console.error("Error al guardar regla:", err);
      const msg = err.response?.data?.message || err.message || "Error al guardar la regla";
      toast.error(msg);
    } finally {
      setSavingRule(false);
    }
  };

  // Confirmar y Ejecutar Eliminación
  const handleDeleteClick = (rule) => {
    setRuleToDelete(rule);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!ruleToDelete) return;
    const id = ruleToDelete._id || ruleToDelete.id;
    setDeletingRule(true);
    try {
      await redirectRulesService.deleteRule(id);
      toast.success(`Regla "${ruleToDelete.name}" eliminada`);
      setDeleteConfirmOpen(false);
      setRuleToDelete(null);
      fetchRules();
    } catch (err) {
      console.error("Error al eliminar regla:", err);
      toast.error(err.response?.data?.message || "No se pudo eliminar la regla");
    } finally {
      setDeletingRule(false);
    }
  };

  // Helper para copiar URL al portapapeles
  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.info("URL copiada al portapapeles");
  };

  // Obtener metadata del tipo de regla
  const getRuleTypeMeta = (type) => {
    return (
      RULE_TYPES.find((t) => t.value === type) || {
        value: type,
        label: type,
        color: "#64748b",
        bg: "#f1f5f9",
      }
    );
  };

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        backgroundColor: "#f8fafc",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <ToastContainer position="top-right" autoClose={3500} hideProgressBar={false} />

      {/* HEADER PRINCIPAL */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          p: 3,
          background: "linear-gradient(135deg, #008069 0%, #005a49 100%)",
          color: "#ffffff",
          boxShadow: "0 10px 25px -5px rgba(0, 128, 105, 0.25)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
        >
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
              <Box
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  p: 1,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CallSplitIcon sx={{ fontSize: 28, color: "#ffffff" }} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: "-0.02em" }}>
                  Redirección de Mensajes (Webhooks)
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  Reenvía mensajes entrantes de WhatsApp en tiempo real hacia servidores, CRMs o servicios externos.
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Stack direction="row" spacing={1.5}>
            <Tooltip title="Actualizar lista de reglas">
              <Button
                variant="outlined"
                onClick={fetchRules}
                disabled={loading}
                sx={{
                  color: "#ffffff",
                  borderColor: "rgba(255,255,255,0.4)",
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 2,
                  "&:hover": {
                    borderColor: "#ffffff",
                    backgroundColor: "rgba(255,255,255,0.1)",
                  },
                }}
                startIcon={<RefreshIcon className={loading ? "animate-spin" : ""} />}
              >
                Refrescar
              </Button>
            </Tooltip>
            <Button
              variant="contained"
              onClick={handleOpenCreateModal}
              startIcon={<AddIcon />}
              sx={{
                bgcolor: "#ffffff",
                color: "#008069",
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 2,
                px: 2.5,
                boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
                "&:hover": {
                  bgcolor: "#f0fdf4",
                  color: "#006d59",
                },
              }}
            >
              Nueva Regla
            </Button>
          </Stack>
        </Stack>
      </Card>

      {/* METRICS / STATS CARDS */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              p: 2.5,
              border: "1px solid #e2e8f0",
              bgcolor: "#ffffff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                Total de Reglas
              </Typography>
              <Typography variant="h4" fontWeight={800} color="#0f172a">
                {stats.total}
              </Typography>
            </Box>
            <Box
              sx={{
                p: 1.5,
                borderRadius: "50%",
                bgcolor: "#e2e8f0",
                color: "#475569",
                display: "flex",
              }}
            >
              <TuneIcon />
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              p: 2.5,
              border: "1px solid #bbf7d0",
              bgcolor: "#f0fdf4",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="caption" color="#166534" fontWeight={600} textTransform="uppercase">
                Reglas Activas
              </Typography>
              <Typography variant="h4" fontWeight={800} color="#15803d">
                {stats.activeCount}
              </Typography>
            </Box>
            <Box
              sx={{
                p: 1.5,
                borderRadius: "50%",
                bgcolor: "#bbf7d0",
                color: "#166534",
                display: "flex",
              }}
            >
              <CheckCircleIcon />
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              p: 2.5,
              border: "1px solid #fed7aa",
              bgcolor: "#fffbeb",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="caption" color="#9a3412" fontWeight={600} textTransform="uppercase">
                Reglas Pausadas
              </Typography>
              <Typography variant="h4" fontWeight={800} color="#c2410c">
                {stats.inactiveCount}
              </Typography>
            </Box>
            <Box
              sx={{
                p: 1.5,
                borderRadius: "50%",
                bgcolor: "#fed7aa",
                color: "#9a3412",
                display: "flex",
              }}
            >
              <ErrorIcon />
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* BARRA DE BÚSQUEDA Y FILTROS */}
      <Card elevation={0} sx={{ borderRadius: 3, p: 2, border: "1px solid #e2e8f0", bgcolor: "#ffffff" }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por nombre, descripción, URL destino o patrón..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm("")}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="status-filter-label">Estado</InputLabel>
              <Select
                labelId="status-filter-label"
                value={statusFilter}
                label="Estado"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">Todos los estados</MenuItem>
                <MenuItem value="true">Solo Activas</MenuItem>
                <MenuItem value="false">Solo Inactivas</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="type-filter-label">Tipo de Coincidencia</InputLabel>
              <Select
                labelId="type-filter-label"
                value={typeFilter}
                label="Tipo de Coincidencia"
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="all">Todos los tipos</MenuItem>
                {RULE_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Card>

      {/* TABLA PRINCIPAL DE REGLAS */}
      <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e2e8f0", bgcolor: "#ffffff", overflow: "hidden" }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
            <CircularProgress sx={{ color: "#008069" }} />
          </Box>
        )}

        {error && !loading && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error" action={<Button onClick={fetchRules}>Reintentar</Button>}>
              {error}
            </Alert>
          </Box>
        )}

        {!loading && !error && filteredRules.length === 0 && (
          <Box sx={{ textAlign: "center", py: 8, px: 3 }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                bgcolor: "#f1f5f9",
                borderRadius: "50%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#64748b",
                mb: 2,
              }}
            >
              <CallSplitIcon sx={{ fontSize: 36 }} />
            </Box>
            <Typography variant="h6" fontWeight={700} color="#1e293b" gutterBottom>
              No se encontraron reglas de redirección
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 460, mx: "auto", mb: 3 }}>
              Crea tu primera regla para que los mensajes entrantes de WhatsApp sean reenviados automáticamente hacia tu
              CRM, bots o sistemas de cotización externos.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateModal}
              sx={{
                bgcolor: "#008069",
                "&:hover": { bgcolor: "#006d59" },
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Crear Regla de Redirección
            </Button>
          </Box>
        )}

        {!loading && !error && filteredRules.length > 0 && (
          <>
            <TableContainer>
              <Table sx={{ minWidth: 850 }}>
                <TableHead sx={{ bgcolor: "#f8fafc" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: "#475569", width: 90 }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#475569" }}>Regla / Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#475569" }}>Tipo & Coincidencia</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#475569" }}>Destino Webhook</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#475569" }}>Filtros & Formato</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#475569", textAlign: "right" }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRules
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((rule) => {
                      const id = rule._id || rule.id;
                      const typeMeta = getRuleTypeMeta(rule.ruleType);
                      const isRowTesting = testingRowId === id;

                      return (
                        <TableRow
                          key={id}
                          hover
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                            opacity: rule.isActive ? 1 : 0.7,
                            transition: "background-color 0.2s ease",
                          }}
                        >
                          {/* ESTADO CON SWITCH */}
                          <TableCell>
                            <Tooltip title={rule.isActive ? "Desactivar regla" : "Activar regla"}>
                              <Switch
                                size="small"
                                checked={Boolean(rule.isActive)}
                                onChange={() => handleToggleStatus(rule)}
                                sx={{
                                  "& .MuiSwitch-switchBase.Mui-checked": {
                                    color: "#008069",
                                  },
                                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                    backgroundColor: "#008069",
                                  },
                                }}
                              />
                            </Tooltip>
                            <Typography
                              variant="caption"
                              display="block"
                              fontWeight={700}
                              sx={{
                                color: rule.isActive ? "#16a34a" : "#94a3b8",
                                fontSize: "0.68rem",
                              }}
                            >
                              {rule.isActive ? "ACTIVA" : "PAUSA"}
                            </Typography>
                          </TableCell>

                          {/* NOMBRE & DESCRIPCIÓN */}
                          <TableCell>
                            <Typography variant="subtitle2" fontWeight={700} color="#0f172a">
                              {rule.name}
                            </Typography>
                            {rule.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem", mt: 0.25 }}>
                                {rule.description}
                              </Typography>
                            )}
                            {rule.stopOnMatch && (
                              <Chip
                                label="Detiene flujo al coincidir"
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: "0.65rem",
                                  mt: 0.5,
                                  bgcolor: "#fef3c7",
                                  color: "#b45309",
                                  fontWeight: 600,
                                }}
                              />
                            )}
                          </TableCell>

                          {/* TIPO & PATRÓN */}
                          <TableCell>
                            <Box sx={{ mb: 0.5 }}>
                              <Chip
                                label={typeMeta.label}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "0.72rem",
                                  bgcolor: typeMeta.bg,
                                  color: typeMeta.color,
                                  border: `1px solid ${typeMeta.color}30`,
                                }}
                              />
                            </Box>

                            {/* Detalle del patrón según el tipo */}
                            {rule.ruleType === "all" ? (
                              <Typography variant="caption" color="text.secondary">
                                Reenvío incondicional (100% mensajes)
                              </Typography>
                            ) : (
                              <Stack spacing={0.5}>
                                {rule.matchPattern && (
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <CodeIcon sx={{ fontSize: 14, color: "#64748b" }} />
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontFamily: "monospace",
                                        bgcolor: "#f1f5f9",
                                        px: 0.8,
                                        py: 0.2,
                                        borderRadius: 1,
                                        color: "#334155",
                                        fontSize: "0.75rem",
                                        maxWidth: 220,
                                        textOverflow: "ellipsis",
                                        overflow: "hidden",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {rule.matchPattern}
                                      {rule.isRegex && rule.regexFlags ? ` /${rule.regexFlags}` : ""}
                                    </Typography>
                                  </Box>
                                )}

                                {Array.isArray(rule.matchPatternList) && rule.matchPatternList.length > 0 && (
                                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                    {rule.matchPatternList.slice(0, 3).map((kw, i) => (
                                      <Chip
                                        key={i}
                                        label={kw}
                                        size="small"
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: "0.68rem" }}
                                      />
                                    ))}
                                    {rule.matchPatternList.length > 3 && (
                                      <Typography variant="caption" color="text.secondary">
                                        +{rule.matchPatternList.length - 3} más
                                      </Typography>
                                    )}
                                  </Stack>
                                )}
                              </Stack>
                            )}
                          </TableCell>

                          {/* DESTINO WEBHOOK */}
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                              <Chip
                                label={rule.httpMethod || "POST"}
                                size="small"
                                sx={{
                                  fontWeight: 800,
                                  fontSize: "0.68rem",
                                  bgcolor: rule.httpMethod === "GET" ? "#e0e7ff" : "#dcfce7",
                                  color: rule.httpMethod === "GET" ? "#4338ca" : "#15803d",
                                }}
                              />
                              <Tooltip title={rule.targetUrl}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontFamily: "monospace",
                                    fontSize: "0.8rem",
                                    fontWeight: 600,
                                    color: "#0f172a",
                                    maxWidth: 220,
                                    textOverflow: "ellipsis",
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {rule.targetUrl}
                                </Typography>
                              </Tooltip>
                              <Tooltip title="Copiar URL">
                                <IconButton size="small" onClick={() => handleCopyUrl(rule.targetUrl)}>
                                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            </Stack>

                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="caption" color="text.secondary">
                                Timeout: {rule.timeoutMs || 5000}ms
                              </Typography>
                              {Array.isArray(rule.headers) && rule.headers.length > 0 && (
                                <Tooltip
                                  title={
                                    <Box>
                                      {rule.headers.map((h, i) => (
                                        <Typography key={i} variant="caption" display="block">
                                          {h.key}: {h.value ? "••••" : "(vacío)"}
                                        </Typography>
                                      ))}
                                    </Box>
                                  }
                                >
                                  <Chip
                                    label={`${rule.headers.length} headers`}
                                    size="small"
                                    sx={{ height: 18, fontSize: "0.65rem", bgcolor: "#f1f5f9" }}
                                  />
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>

                          {/* FILTROS & FORMATO */}
                          <TableCell>
                            <Stack spacing={0.5}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Formato:
                                </Typography>
                                <Chip
                                  label={(rule.payloadFormat || "raw").toUpperCase()}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700 }}
                                />
                              </Box>

                              {rule.senderFilter && (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                  <PhoneIcon sx={{ fontSize: 13, color: "#64748b" }} />
                                  <Typography variant="caption" fontWeight={600} color="#475569">
                                    {rule.senderFilter}
                                  </Typography>
                                </Box>
                              )}

                              {Array.isArray(rule.messageTypes) && rule.messageTypes.length > 0 ? (
                                <Typography variant="caption" color="text.secondary">
                                  Tipos: {rule.messageTypes.join(", ")}
                                </Typography>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  Todos los tipos
                                </Typography>
                              )}
                            </Stack>
                          </TableCell>

                          {/* ACCIONES */}
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                              <Tooltip title="Probar conexión en vivo">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleTestRowConnection(rule)}
                                    disabled={isRowTesting}
                                    sx={{
                                      color: "#0284c7",
                                      bgcolor: "#e0f2fe",
                                      "&:hover": { bgcolor: "#bae6fd" },
                                    }}
                                  >
                                    {isRowTesting ? (
                                      <CircularProgress size={16} sx={{ color: "#0284c7" }} />
                                    ) : (
                                      <PlayArrowIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>

                              <Tooltip title="Editar regla">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenEditModal(rule)}
                                  sx={{
                                    color: "#475569",
                                    bgcolor: "#f1f5f9",
                                    "&:hover": { bgcolor: "#e2e8f0" },
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Eliminar regla">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteClick(rule)}
                                  sx={{
                                    color: "#dc2626",
                                    bgcolor: "#fee2e2",
                                    "&:hover": { bgcolor: "#fecaca" },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredRules.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="Filas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </>
        )}
      </Card>

      {/* DIÁLOGO / MODAL DE CREACIÓN / EDICIÓN */}
      <Dialog
        open={openModal}
        onClose={() => !savingRule && setOpenModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #008069 0%, #005a49 100%)",
            color: "#ffffff",
            p: 2.5,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {isEditing ? "Editar Regla de Redirección" : "Nueva Regla de Redirección Webhook"}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                Configure las condiciones de disparo y el endpoint receptor de los mensajes
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setOpenModal(false)}
              disabled={savingRule}
              sx={{ color: "#ffffff" }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        {/* PESTAÑAS DEL MODAL */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "#f8fafc" }}>
          <Tabs
            value={modalTab}
            onChange={(e, newVal) => setModalTab(newVal)}
            textColor="inherit"
            sx={{
              "& .MuiTabs-indicator": { backgroundColor: "#008069" },
              "& .Mui-selected": { color: "#008069", fontWeight: 700 },
            }}
          >
            <Tab label="1. Destino y Conexión" icon={<HttpIcon fontSize="small" />} iconPosition="start" />
            <Tab label="2. Criterios de Coincidencia" icon={<TuneIcon fontSize="small" />} iconPosition="start" />
            <Tab label="3. Filtros y Formato" icon={<FilterListIcon fontSize="small" />} iconPosition="start" />
          </Tabs>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          {/* TAB 0: DESTINO Y CONEXIÓN */}
          {modalTab === 0 && (
            <Stack spacing={3}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    label="Nombre Descriptivo de la Regla"
                    placeholder="Ej: Webhook Cotizaciones Autos o CRM Externo"
                    value={formData.name}
                    onChange={(e) => handleFormChange("name", e.target.value)}
                    required
                    helperText="Nombre identificador en el panel"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isActive}
                        onChange={(e) => handleFormChange("isActive", e.target.checked)}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#008069",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: "#008069",
                          },
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {formData.isActive ? "Regla Activa" : "Regla Inactiva"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formData.isActive ? "Evaluando mensajes" : "En pausa"}
                        </Typography>
                      </Box>
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Descripción (Opcional)"
                    placeholder="Ej: Redirige mensajes de consulta vehicular a CRM Salesforce"
                    value={formData.description}
                    onChange={(e) => handleFormChange("description", e.target.value)}
                  />
                </Grid>
              </Grid>

              <Divider />

              {/* URL Y CONECTIVIDAD */}
              <Typography variant="subtitle2" fontWeight={700} color="#0f172a">
                Configuración del Servidor Destino (Webhook)
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth>
                    <InputLabel id="http-method-label">Método HTTP</InputLabel>
                    <Select
                      labelId="http-method-label"
                      value={formData.httpMethod}
                      label="Método HTTP"
                      onChange={(e) => handleFormChange("httpMethod", e.target.value)}
                    >
                      <MenuItem value="POST">POST (Recomendado)</MenuItem>
                      <MenuItem value="GET">GET</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="URL Destino (Target URL)"
                    placeholder="https://mi-crm.com/api/webhook"
                    value={formData.targetUrl}
                    onChange={(e) => handleFormChange("targetUrl", e.target.value)}
                    required
                    helperText="Endpoint HTTPS que recibirá el mensaje"
                  />
                </Grid>

                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Timeout (ms)"
                    value={formData.timeoutMs}
                    onChange={(e) => handleFormChange("timeoutMs", e.target.value)}
                    helperText="Tiempo máx. espera (ej: 5000)"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">ms</InputAdornment>,
                    }}
                  />
                </Grid>
              </Grid>

              {/* CABECERAS HTTP (HEADERS) */}
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600} color="#334155">
                    Cabeceras HTTP Personalizadas (Headers)
                  </Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={handleAddHeader} sx={{ textTransform: "none" }}>
                    Añadir Header
                  </Button>
                </Stack>

                <Stack spacing={1.5}>
                  {formData.headers.map((h, index) => (
                    <Grid container spacing={1.5} alignItems="center" key={index}>
                      <Grid item xs={5}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Header Key (ej: Authorization, X-Api-Key)"
                          value={h.key}
                          onChange={(e) => handleHeaderChange(index, "key", e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Value (ej: Bearer mi-token-secreto)"
                          value={h.value}
                          onChange={(e) => handleHeaderChange(index, "value", e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={1}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveHeader(index)}
                          disabled={formData.headers.length === 1 && !h.key}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}
                </Stack>
              </Box>

              {/* BOTÓN DE TEST DE CONEXIÓN */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: "#f1f5f9",
                  borderRadius: 2,
                  border: "1px dashed #cbd5e1",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Probar Conectividad con el Webhook
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Envía un ping de prueba a la URL destino para certificar que responde antes de guardar.
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={
                      testingConnection ? (
                        <CircularProgress size={14} sx={{ color: "#008069" }} />
                      ) : (
                        <SpeedIcon />
                      )
                    }
                    onClick={handleTestConnectionInModal}
                    disabled={testingConnection || !formData.targetUrl.trim()}
                    sx={{
                      borderColor: "#008069",
                      color: "#008069",
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    {testingConnection ? "Probando..." : "Probar Ahora"}
                  </Button>
                </Stack>

                {testResult && (
                  <Alert
                    severity={
                      testResult.success || (testResult.statusCode >= 200 && testResult.statusCode < 300)
                        ? "success"
                        : "error"
                    }
                    sx={{ mt: 1 }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {testResult.success || (testResult.statusCode >= 200 && testResult.statusCode < 300)
                        ? `Conexión Exitosa — Código ${testResult.statusCode || 200} en ${testResult.durationMs || 0}ms`
                        : `Fallo de Conexión: ${testResult.error || `HTTP ${testResult.statusCode}`}`}
                    </Typography>
                  </Alert>
                )}
              </Box>
            </Stack>
          )}

          {/* TAB 1: CRITERIOS DE COINCIDENCIA */}
          {modalTab === 1 && (
            <Stack spacing={3}>
              <Typography variant="subtitle2" fontWeight={700} color="#0f172a">
                Seleccione la Condición de Disparo (Rule Type)
              </Typography>

              <Grid container spacing={2}>
                {RULE_TYPES.map((rt) => {
                  const isSelected = formData.ruleType === rt.value;
                  return (
                    <Grid item xs={12} sm={6} key={rt.value}>
                      <Card
                        elevation={0}
                        onClick={() => {
                          handleFormChange("ruleType", rt.value);
                          if (rt.value === "text_regex") {
                            handleFormChange("isRegex", true);
                          }
                        }}
                        sx={{
                          p: 2,
                          cursor: "pointer",
                          borderRadius: 2,
                          border: isSelected ? `2px solid #008069` : `1px solid #e2e8f0`,
                          bgcolor: isSelected ? "#f0fdf4" : "#ffffff",
                          transition: "all 0.15s ease",
                          "&:hover": {
                            borderColor: "#008069",
                            bgcolor: isSelected ? "#f0fdf4" : "#f8fafc",
                          },
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle2" fontWeight={700} color={isSelected ? "#008069" : "#1e293b"}>
                            {rt.label}
                          </Typography>
                          {isSelected && <CheckCircleIcon sx={{ color: "#008069", fontSize: 20 }} />}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                          {rt.desc}
                        </Typography>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>

              <Divider />

              {/* CAMPOS DEPENDIENTES DEL TIPO */}
              {formData.ruleType !== "all" && (
                <Stack spacing={2.5}>
                  <Typography variant="subtitle2" fontWeight={700} color="#0f172a">
                    Parámetros de Coincidencia
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={formData.isRegex ? 8 : 12}>
                      <TextField
                        fullWidth
                        label="Patrón de Coincidencia (matchPattern)"
                        placeholder={
                          formData.ruleType === "text_regex"
                            ? "^cotizar\\s+(auto|carro)"
                            : formData.ruleType === "text_exact"
                            ? "cotizar auto"
                            : "auto"
                        }
                        value={formData.matchPattern}
                        onChange={(e) => handleFormChange("matchPattern", e.target.value)}
                        helperText={
                          formData.ruleType === "text_regex"
                            ? "Expresión regular que se evaluará sobre el contenido del mensaje"
                            : "Texto que debe coincidir en el mensaje"
                        }
                      />
                    </Grid>

                    {formData.isRegex && (
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Flags Regex (regexFlags)"
                          placeholder="i"
                          value={formData.regexFlags}
                          onChange={(e) => handleFormChange("regexFlags", e.target.value)}
                          helperText="'i' (insensible a mayúsculas), 'm' (multilínea)"
                        />
                      </Grid>
                    )}
                  </Grid>

                  <Stack direction="row" spacing={3}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.isRegex}
                          onChange={(e) => handleFormChange("isRegex", e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            Evaluar con Regex
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Activa el motor de expresiones regulares
                          </Typography>
                        </Box>
                      }
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.caseSensitive}
                          onChange={(e) => handleFormChange("caseSensitive", e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            Sensible a Mayúsculas
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Distingue entre minúsculas y MAYÚSCULAS
                          </Typography>
                        </Box>
                      }
                    />
                  </Stack>

                  {/* LISTA DE PALABRAS CLAVE (matchPatternList) */}
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600} color="#334155" sx={{ mb: 1 }}>
                      Lista Opcional de Palabras Clave (matchPatternList)
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                      Útil para 'text_contains' o 'any_match': el mensaje coincidirá si incluye cualquiera de estas palabras.
                    </Typography>

                    <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                      <TextField
                        size="small"
                        placeholder="Escriba palabra clave y pulse Enter"
                        value={keywordInput}
                        onChange={(e) => setKeywordInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddKeyword();
                          }
                        }}
                        sx={{ maxWidth: 350 }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleAddKeyword}
                        disabled={!keywordInput.trim()}
                        sx={{ textTransform: "none" }}
                      >
                        Añadir Palabra
                      </Button>
                    </Stack>

                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {formData.matchPatternList.map((kw, i) => (
                        <Chip
                          key={i}
                          label={kw}
                          onDelete={() => handleRemoveKeyword(kw)}
                          color="primary"
                          variant="outlined"
                          sx={{ mb: 0.5 }}
                        />
                      ))}
                      {formData.matchPatternList.length === 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                          Sin palabras clave adicionales añadidas.
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                </Stack>
              )}

              {/* DETENER FLUJO (stopOnMatch) */}
              <Box sx={{ p: 2, bgcolor: "#fffbeb", borderRadius: 2, border: "1px solid #fef3c7" }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.stopOnMatch}
                      onChange={(e) => handleFormChange("stopOnMatch", e.target.checked)}
                      color="warning"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={700} color="#92400e">
                        Detener Evaluación tras Coincidir (stopOnMatch)
                      </Typography>
                      <Typography variant="caption" color="#b45309">
                        Si se activa, ninguna regla posterior será procesada para este mensaje tras ejecutarse esta redirección.
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </Stack>
          )}

          {/* TAB 2: FILTROS Y FORMATO */}
          {modalTab === 2 && (
            <Stack spacing={3}>
              {/* FORMATO DEL PAYLOAD */}
              <Box>
                <Typography variant="subtitle2" fontWeight={700} color="#0f172a" sx={{ mb: 1.5 }}>
                  Formato del Payload Enviado al Webhook (payloadFormat)
                </Typography>
                <Grid container spacing={2}>
                  {[
                    {
                      id: "raw",
                      title: "RAW (Recomendado)",
                      desc: "Envía el payload JSON original intacto de WhatsApp Cloud API.",
                    },
                    {
                      id: "standard",
                      title: "STANDARD",
                      desc: "Envía un formato estructurado normalizado (from, text, timestamp, etc.).",
                    },
                    {
                      id: "both",
                      title: "BOTH",
                      desc: "Envía tanto el payload normalizado como el raw anidado.",
                    },
                  ].map((format) => {
                    const isSelected = formData.payloadFormat === format.id;
                    return (
                      <Grid item xs={12} sm={4} key={format.id}>
                        <Card
                          elevation={0}
                          onClick={() => handleFormChange("payloadFormat", format.id)}
                          sx={{
                            p: 2,
                            cursor: "pointer",
                            borderRadius: 2,
                            border: isSelected ? "2px solid #008069" : "1px solid #e2e8f0",
                            bgcolor: isSelected ? "#f0fdf4" : "#ffffff",
                            "&:hover": { borderColor: "#008069" },
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            color={isSelected ? "#008069" : "#1e293b"}
                          >
                            {format.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                            {format.desc}
                          </Typography>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>

              <Divider />

              {/* FILTRO POR REMITENTE */}
              <Box>
                <Typography variant="subtitle2" fontWeight={700} color="#0f172a" sx={{ mb: 1 }}>
                  Filtro por Remitente o Prefijo Telefónico (senderFilter)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                  Deje vacío para aceptar todos los remitentes. Soporta prefijos con comodín (ej: <code>507*</code> para
                  Panamá, <code>1*</code> para EE.UU./Canadá, o un número completo <code>50766289358</code>).
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Ej: 507* o 50766289358 (Opcional)"
                  value={formData.senderFilter}
                  onChange={(e) => handleFormChange("senderFilter", e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ maxWidth: 400 }}
                />
              </Box>

              <Divider />

              {/* TIPOS DE MENSAJE (messageTypes) */}
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700} color="#0f172a">
                    Filtro por Tipo de Mensaje (messageTypes)
                  </Typography>
                  <Button
                    size="small"
                    onClick={() =>
                      handleFormChange(
                        "messageTypes",
                        formData.messageTypes.length === AVAILABLE_MESSAGE_TYPES.length
                          ? []
                          : AVAILABLE_MESSAGE_TYPES.map((t) => t.id)
                      )
                    }
                    sx={{ textTransform: "none" }}
                  >
                    {formData.messageTypes.length === AVAILABLE_MESSAGE_TYPES.length
                      ? "Deseleccionar todos"
                      : "Seleccionar todos"}
                  </Button>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                  Si no selecciona ninguno, la regla aplicará a todos los tipos de mensajes entrantes.
                </Typography>

                <Grid container spacing={1}>
                  {AVAILABLE_MESSAGE_TYPES.map((type) => {
                    const isChecked = formData.messageTypes.includes(type.id);
                    return (
                      <Grid item key={type.id}>
                        <Chip
                          label={type.label}
                          clickable
                          onClick={() => handleToggleMessageType(type.id)}
                          color={isChecked ? "primary" : "default"}
                          variant={isChecked ? "filled" : "outlined"}
                          sx={{
                            fontWeight: isChecked ? 700 : 500,
                            bgcolor: isChecked ? "#008069" : "transparent",
                            "&:hover": {
                              bgcolor: isChecked ? "#006d59" : "#f1f5f9",
                            },
                          }}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2.5, bgcolor: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
          <Button
            onClick={() => setOpenModal(false)}
            disabled={savingRule}
            sx={{ textTransform: "none", color: "#64748b" }}
          >
            Cancelar
          </Button>

          {modalTab > 0 && (
            <Button
              onClick={() => setModalTab((prev) => prev - 1)}
              disabled={savingRule}
              sx={{ textTransform: "none" }}
            >
              Anterior
            </Button>
          )}

          {modalTab < 2 ? (
            <Button
              variant="contained"
              onClick={() => setModalTab((prev) => prev + 1)}
              sx={{
                bgcolor: "#008069",
                "&:hover": { bgcolor: "#006d59" },
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Siguiente
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleSaveRule}
              disabled={savingRule}
              startIcon={
                savingRule ? <CircularProgress size={16} sx={{ color: "#ffffff" }} /> : <CheckIcon />
              }
              sx={{
                bgcolor: "#008069",
                "&:hover": { bgcolor: "#006d59" },
                textTransform: "none",
                fontWeight: 700,
                px: 3,
              }}
            >
              {savingRule ? "Guardando..." : isEditing ? "Actualizar Regla" : "Guardar Regla"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* DIÁLOGO DE CONFIRMACIÓN DE BORRADO */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => !deletingRule && setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#dc2626" }}>¿Eliminar Regla?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            ¿Está seguro de que desea eliminar la regla <strong>"{ruleToDelete?.name}"</strong>?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            Esta acción detendrá de inmediato el reenvío de mensajes hacia <code>{ruleToDelete?.targetUrl}</code> y no
            se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            disabled={deletingRule}
            sx={{ textTransform: "none", color: "#64748b" }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deletingRule}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {deletingRule ? "Eliminando..." : "Eliminar Regla"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
